/**
 * Normalized lead-source taxonomy.
 *
 * The site captures rich first-touch signals (ad click-ids, UTM parameters, an
 * external referrer, a landing page and a `?ref=` referral code) but stores them
 * as raw strings, while staff-facing reporting uses a separate coarse
 * marketing-source enum that is not derived from those signals. This module is
 * the single, canonical "where did this visitor come from" dimension: a coarse
 * {@link SourceChannel}, a specific {@link NormalizedSource.source}, and an
 * optional campaign.
 *
 * The mapping from captured signals to a normalized source lives in a
 * data-driven {@link SOURCE_REGISTRY}: onboarding a brand-new source is a single
 * registry entry with no other code changes (see docs/lead-source-taxonomy.md).
 *
 * PHI-safety: every field here is a coarse marketing label or identifier
 * (channel, network name, UTM campaign/source token) — never a patient name,
 * email, phone number or any free-text health-answer content — so a normalized
 * source is safe to attach to the PHI-safe acquisition super-properties.
 */

/**
 * The controlled vocabulary of coarse acquisition channels. Every normalized
 * source resolves to exactly one of these; adding a genuinely new *channel*
 * (as opposed to a new source within an existing channel) is the only change
 * that touches this list.
 */
export const SOURCE_CHANNELS = [
  'paid_social',
  'paid_search',
  'clinician_referral',
  'charity_referral',
  'webinar',
  'affiliate',
  'experiment',
  'organic',
  'direct',
] as const;

export type SourceChannel = (typeof SOURCE_CHANNELS)[number];

/**
 * A single normalized source: the coarse {@link channel}, a specific
 * {@link source} (a named network, partner, charity or webinar) and the
 * optional {@link campaign} (the UTM campaign token). PHI-safe by construction.
 */
export interface NormalizedSource {
  channel: SourceChannel;
  source: string;
  campaign?: string;
}

/** Cap on any derived source/campaign string — a defensive size guard. */
export const MAX_LABEL_LENGTH = 100;

/**
 * The default source when no signal is present at all. The resolver guarantees
 * it never returns null; an entirely un-channelled first touch is `direct`.
 */
export const DIRECT_SOURCE: NormalizedSource = { channel: 'direct', source: 'Direct' };

/**
 * Where a matched registry entry takes its specific `source` label from when it
 * is not a fixed network name — e.g. a webinar's or charity partner's name lives
 * in the UTM source, an experiment's identity in the UTM campaign.
 */
export type SourceFrom = 'utmSource' | 'utmCampaign' | 'sourceCode';

/**
 * A declarative match spec for a registry entry. All specified criteria must
 * hold (AND); within an array field, any value matches (OR). This is data, not
 * code, so a new source is a new {@link SourceRegistryEntry} — nothing else.
 */
export interface SourceSignalMatch {
  /** Matches when any of these ad click-id params is present on the landing. */
  clickIds?: readonly string[];
  /** Matches when the (lower-cased) utm_source is one of these. */
  utmSource?: readonly string[];
  /** Matches when the (lower-cased) utm_medium is one of these. */
  utmMedium?: readonly string[];
  /**
   * Matches when the (lower-cased) `?src=` source code is one of these. Used to
   * attribute offline / relationship channels (clinician-partner, charity,
   * webinar links) that carry no ad click-id or UTM. A code maps to exactly one
   * named source; onboarding a new named source is a single entry below.
   */
  sourceCode?: readonly string[];
  /**
   * Matches when the first-touch landing path (query-stripped, lower-cased,
   * trailing slash removed) is one of these. Used for tracked alias URLs — a
   * partner links to a dedicated path that renders an existing page, and the
   * path alone identifies the source (no query parameter to lose).
   */
  landingPath?: readonly string[];
  /** Matches on presence/absence of a `?ref=` referral code. */
  hasReferralCode?: boolean;
}

/**
 * One registry entry: a match spec plus the normalized source it resolves to.
 * `source` is the fixed label used unless `sourceFrom` names a captured signal
 * to take a more specific label from (falling back to `source` when absent).
 */
export interface SourceRegistryEntry {
  id: string;
  channel: SourceChannel;
  source: string;
  sourceFrom?: SourceFrom;
  match: SourceSignalMatch;
}

/**
 * The data-driven source registry, in priority order (first match wins).
 *
 * Ad click-ids are listed first: they are the strongest, least-spoofable signal
 * that a visit came from a specific paid network. UTM-tagged campaigns follow
 * (own newsletters, webinars, partner/charity links, experiments), and anything
 * unmatched falls through to the resolver's organic/direct default.
 *
 * To onboard a new source, add one entry here — see docs/lead-source-taxonomy.md.
 */
export const SOURCE_REGISTRY: readonly SourceRegistryEntry[] = [
  // --- Paid search (ad click-ids) ---
  {
    id: 'google-ads',
    channel: 'paid_search',
    source: 'Google',
    match: { clickIds: ['gclid', 'gbraid', 'wbraid'] },
  },
  {
    id: 'microsoft-ads',
    channel: 'paid_search',
    source: 'Microsoft',
    match: { clickIds: ['msclkid'] },
  },

  // --- Paid social (ad click-ids) ---
  {
    id: 'meta-ads',
    channel: 'paid_social',
    source: 'Meta',
    match: { clickIds: ['fbclid'] },
  },
  {
    id: 'tiktok-ads',
    channel: 'paid_social',
    source: 'TikTok',
    match: { clickIds: ['ttclid'] },
  },
  {
    id: 'linkedin-ads',
    channel: 'paid_social',
    source: 'LinkedIn',
    match: { clickIds: ['li_fat_id'] },
  },

  // --- Coded offline / relationship links (?src=<code>) ---
  // Offline, charity and webinar links carry no ad click-id or UTM, so they use
  // a short per-channel source code that maps here to a readable named source. A
  // coded link is a deliberate, explicit signal, so these sit above the generic
  // `?ref=` referral fallback (a bare UUID with no named partner). To onboard a
  // new clinician-partner, charity or webinar, add ONE entry below — no other
  // code changes (see docs/lead-source-taxonomy.md). The codes/names below are
  // the initial seed set; replace them with the real onboarded partners.
  {
    id: 'partner-northside-oncology',
    channel: 'clinician_referral',
    source: 'Northside Oncology',
    match: { sourceCode: ['nso'] },
  },
  {
    id: 'partner-meridian-health-group',
    channel: 'clinician_referral',
    source: 'Meridian Health Group',
    match: { sourceCode: ['mhg'] },
  },
  {
    id: 'charity-cancer-care-alliance',
    channel: 'charity_referral',
    source: 'Cancer Care Alliance',
    match: { sourceCode: ['cca'] },
  },
  {
    id: 'charity-patient-hope-network',
    channel: 'charity_referral',
    source: 'Patient Hope Network',
    match: { sourceCode: ['phn'] },
  },
  {
    id: 'webinar-second-opinion-live',
    channel: 'webinar',
    source: 'Second Opinion Live',
    match: { sourceCode: ['sol'] },
  },
  {
    id: 'webinar-ask-the-oncologist',
    channel: 'webinar',
    source: 'Ask the Oncologist',
    match: { sourceCode: ['ato'] },
  },

  // --- Affiliate tracked URLs (dedicated landing path, or ?src= fallback) ---
  // Tend the Terrain links to `/tend-the-terrain`, which renders the ordinary
  // homepage; the landing path itself is the attribution signal (first-touch
  // only, like every other signal). `?src=ttt` is the fallback link format.
  // Two entries because a match spec ANDs its criteria; both resolve to the
  // identical named source.
  {
    id: 'affiliate-tend-the-terrain-path',
    channel: 'affiliate',
    source: 'Tend the Terrain',
    match: { landingPath: ['/tend-the-terrain'] },
  },
  {
    id: 'affiliate-tend-the-terrain-code',
    channel: 'affiliate',
    source: 'Tend the Terrain',
    match: { sourceCode: ['ttt'] },
  },

  // --- Clinician referral link (?ref=<id>) ---
  {
    id: 'referral-link',
    channel: 'clinician_referral',
    source: 'Referral link',
    match: { hasReferralCode: true },
  },

  // --- Paid campaigns tagged only with UTMs (no click-id captured) ---
  {
    id: 'google-search-utm',
    channel: 'paid_search',
    source: 'Google',
    match: { utmSource: ['google'], utmMedium: ['cpc', 'ppc', 'paid', 'paidsearch', 'sem'] },
  },
  {
    id: 'bing-search-utm',
    channel: 'paid_search',
    source: 'Microsoft',
    match: { utmSource: ['bing', 'microsoft'], utmMedium: ['cpc', 'ppc', 'paid', 'paidsearch', 'sem'] },
  },
  {
    id: 'meta-social-utm',
    channel: 'paid_social',
    source: 'Meta',
    match: {
      utmSource: ['facebook', 'meta', 'instagram', 'fb', 'ig'],
      utmMedium: ['paid_social', 'paidsocial', 'cpc', 'social'],
    },
  },

  // --- Staff manual import (ops lead desk — no browser-side match ever fires) ---
  // A lead imported via POST /leads/review/create is stored with source_name =
  // 'Manual import' and source_channel = 'direct' directly by the endpoint; the
  // match spec here is never evaluated at runtime. The entry exists so the lead
  // desk's source chip and the source-performance rollup table label the row
  // correctly (both are driven by stored source_name values, not registry
  // evaluation).
  {
    id: 'manual-import',
    channel: 'direct',
    source: 'Manual import',
    match: { sourceCode: ['manual-import'] },
  },

  // --- Owned / partner UTM channels (specific source taken from the tag) ---
  {
    id: 'webinar',
    channel: 'webinar',
    source: 'Webinar',
    sourceFrom: 'utmSource',
    match: { utmMedium: ['webinar'] },
  },
  {
    id: 'charity-partner',
    channel: 'charity_referral',
    source: 'Charity partner',
    sourceFrom: 'utmSource',
    match: { utmMedium: ['charity', 'partner'] },
  },
  {
    id: 'experiment',
    channel: 'experiment',
    source: 'Experiment',
    sourceFrom: 'utmCampaign',
    match: { utmMedium: ['experiment', 'ab-test', 'abtest'] },
  },
];
